/**
 * API Settings — types & helpers for the Integriti / LoopLearnAPI integration.
 *
 * Ported from the legacy html/api_settings.html inline script. The page reads
 * the tenant credential, decides whether an API user already exists, and lets
 * the admin generate or delete the API key.
 */

/** A single settings block inside the credential's sis_sync_settings. */
export type ApiSetting = {
  api_key_id?: string;
  [key: string]: unknown;
};

type CredentialLike = {
  sis_sync_settings?: Array<{
    user_agent?: string;
    settings?: ApiSetting | ApiSetting[];
  }>;
};

/**
 * Flatten the `settings` of every LoopLearnAPI sync-settings entry.
 * Equivalent to the legacy underscore chain:
 *   _.flatten(_.pluck(_.where(cred.sis_sync_settings, {user_agent:"LoopLearnAPI"}), "settings"))
 */
export function extractApiSettings(
  credential: CredentialLike | null | undefined
): ApiSetting[] {
  const entries = credential?.sis_sync_settings ?? [];
  return entries
    .filter((e) => e?.user_agent === "LoopLearnAPI")
    .map((e) => e?.settings)
    .reduce<ApiSetting[]>((acc, s) => {
      if (Array.isArray(s)) acc.push(...s);
      else if (s) acc.push(s);
      return acc;
    }, []);
}

/**
 * Whether an API key already exists (→ show Delete) or not (→ show Generate).
 * Preserves the legacy rule: a key is considered present unless `api_key_id`
 * exists AND is an empty string.
 */
export function hasApiKey(setting: ApiSetting): boolean {
  const hasProp = "api_key_id" in setting;
  return (hasProp && setting.api_key_id !== "") || !hasProp;
}

export type NewApiUser = {
  username: string;
  email: string;
  password: string;
  confirm_password: string;
};

export const initialNewApiUser: NewApiUser = {
  username: "",
  email: "",
  password: "",
  confirm_password: "",
};

/**
 * Amazon Cognito's default password policy. The API user is created in Cognito,
 * so the form enforces the same rules up-front to avoid a round-trip rejection:
 * min 8 chars, plus at least one uppercase, lowercase, number and symbol.
 */
export type PasswordRule = { label: string; test: (value: string) => boolean };

export const PASSWORD_RULES: PasswordRule[] = [
  { label: "At least 8 characters", test: (v) => v.length >= 8 },
  { label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v) => /[a-z]/.test(v) },
  { label: "One number", test: (v) => /[0-9]/.test(v) },
  { label: "One special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

/** True when the password satisfies every Cognito policy rule. */
export const passwordMeetsPolicy = (value: string): boolean =>
  PASSWORD_RULES.every((rule) => rule.test(value));
